import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiEdit2, FiInfo, FiPrinter, FiRepeat, FiTrash2 } from 'react-icons/fi';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { rightsOf, useSessionUser } from '../../lib/permissions';
import { formatDate, timeAgo } from '../../lib/format';
import { BUTTON, CARD, EmptyState, Field, Modal, Notice, PageHeader, Panel, Pill, Select, SIZE, SkeletonRows } from '../ui/kit';
import { SECTIONS, UNITS, ageLabel, birthDateOf, hasValue, splitList, statusLabel, statusTone } from './patientModel';
import { deletePatient, errorMessage, fetchPatient, patientAccess, transferPatient } from './api';
import PatientNotes from './PatientNotes';
import PatientFiles from '../fichiers/PatientFiles';
import PatientStaff from '../staff/PatientStaff';
import PatientEchos from '../echo/PatientEchos';
import PatientCros from '../cro/PatientCros';

const optionLabel = (field, value) => {
  const options = field.options || [];
  const match = options.find((option) => typeof option === 'object' && option.value === value);
  return match ? match.label : value;
};

const FieldValue = ({ field, value }) => {
  if (field.type === 'multi') {
    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        {splitList(value).map((item) => (
          <Pill key={item}>{item}</Pill>
        ))}
      </div>
    );
  }
  if (field.type === 'date') return <span>{formatDate(value) || value}</span>;
  if (field.name === 'statut') return <span>{statusLabel(value)}</span>;
  if (field.type === 'textarea') return <p className="m-0 whitespace-pre-wrap break-words leading-relaxed">{value}</p>;
  return <span className="break-words">{optionLabel(field, value)}</span>;
};

const valueOf = (patient, field) => (field.name === 'dateNaissance' ? birthDateOf(patient) : patient[field.name]);

const SectionPanel = ({ section, patient }) => {
  const filled = section.fields.filter((field) => hasValue(valueOf(patient, field)));
  if (!filled.length) return null;
  return (
    <Panel title={section.title}>
      <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-[14px] sm:grid-cols-2">
        {filled.map((field) => (
          <div key={field.name} className={field.wide || field.type === 'textarea' || field.type === 'multi' ? 'sm:col-span-2' : ''}>
            <dt className="text-[12.5px] font-medium text-black/45">{field.label}</dt>
            <dd className="mb-0 mt-0.5 text-[14.5px] text-ink">
              <FieldValue field={field} value={valueOf(patient, field)} />
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
};

const Fact = ({ label, value }) => (
  <div className="min-w-0 px-[16px] py-[14px]">
    <dt className="text-[12px] font-medium text-black/45">{label}</dt>
    <dd className="mb-0 mt-0.5 truncate text-[15px] font-semibold text-ink">{value || '—'}</dd>
  </div>
);

const PatientRecord = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const justSaved = Boolean(location.state && location.state.saved);
  const filesNotAttached = Boolean(location.state && location.state.filesFailed);
  const user = useSessionUser();
  const rights = rightsOf(user);
  const { patients, updatePatients } = usePatientDoctorContext();
  const cached = patients.find((item) => item._id === id);
  const [patient, setPatient] = useState(cached || null);
  const [status, setStatus] = useState(cached ? 'ready' : 'loading');
  const [dialog, setDialog] = useState(null);
  const [unit, setUnit] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchPatient(id)
      .then((data) => {
        if (cancelled) return;
        setPatient(data);
        setStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        setStatus(error.response && error.response.status === 404 ? 'missing' : cached ? 'ready' : 'error');
      });
    return () => {
      cancelled = true;
    };
    // The cached copy only fills the screen while the fresh file loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const replaceInList = (updated) => updatePatients(patients.map((item) => (item._id === updated._id ? updated : item)));

  const openDialog = (name) => {
    setDialogError('');
    setUnit('');
    setDialog(name);
  };

  const transfer = async () => {
    if (!unit || busy) return;
    setBusy(true);
    setDialogError('');
    try {
      const updated = await transferPatient(patient._id, unit);
      setPatient(updated);
      replaceInList(updated);
      setDialog(null);
    } catch (error) {
      setDialogError(errorMessage(error, 'Le transfert n’a pas pu être enregistré.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setDialogError('');
    try {
      await deletePatient(patient._id);
      updatePatients(patients.filter((item) => item._id !== patient._id));
      navigate('/patients', { replace: true });
    } catch (error) {
      setDialogError(errorMessage(error, 'Le dossier n’a pas pu être supprimé.'));
      setBusy(false);
    }
  };

  const back = { to: '/patients', label: 'Dossiers patients' };

  if (status === 'loading') {
    return (
      <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
        <PageHeader back={back} title="Chargement du dossier…" />
        <div className={`${CARD} mt-8 p-6`}>
          <SkeletonRows rows={4} />
        </div>
      </main>
    );
  }
  if (status !== 'ready' || !patient) {
    return (
      <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
        <PageHeader back={back} title="Dossier introuvable" />
        <div className={`${CARD} mt-8`}>
          <EmptyState icon={FiAlertCircle} title={status === 'missing' ? 'Ce dossier n’existe pas ou a été supprimé.' : 'Le dossier n’a pas pu être chargé.'}>
            Revenez à la liste des dossiers pour en ouvrir un autre.
          </EmptyState>
        </div>
      </main>
    );
  }

  const access = patientAccess(user, patient);
  const clinicalEmpty = SECTIONS.filter((section) => section.clinical).every((section) =>
    section.fields.every((field) => !hasValue(patient[field.name]))
  );
  const identityMeta = [patient.dossierNumber && `Dossier ${patient.dossierNumber}`, patient.services && `Unité ${patient.services}`, patient.lit]
    .filter(Boolean)
    .join(' · ');

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        back={back}
        title={patient.nom || 'Nom non précisé'}
        subtitle={
          <span className="flex flex-col gap-2">
            <span>{identityMeta || 'Dossier sans numéro'}</span>
            <span className="flex flex-wrap gap-1.5">
              {patient.statut && <Pill tone={statusTone(patient.statut)}>{statusLabel(patient.statut)}</Pill>}
              {patient.validation && <Pill tone={patient.validation === 'Validé' ? 'green' : 'amber'}>{patient.validation}</Pill>}
              {patient.urgence === 'oui' && <Pill tone="red">Urgence</Pill>}
              {patient['spécialité'] && <Pill>{patient['spécialité']}</Pill>}
            </span>
          </span>
        }
        actions={
          <>
            {access.canPrint && (
              <button type="button" onClick={() => window.print()} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiPrinter size={15} aria-hidden="true" />
                Imprimer
              </button>
            )}
            {access.canEdit && (
              <button type="button" onClick={() => openDialog('transfer')} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiRepeat size={15} aria-hidden="true" />
                Transférer
              </button>
            )}
            {access.canDelete && (
              <button type="button" onClick={() => openDialog('delete')} className={`${BUTTON.danger} ${SIZE.md}`}>
                <FiTrash2 size={15} aria-hidden="true" />
                Supprimer
              </button>
            )}
            {access.canEdit && (
              <Link to={`/patients/${patient._id}/modifier`} className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiEdit2 size={15} aria-hidden="true" />
                Modifier
              </Link>
            )}
          </>
        }
      />

      {justSaved && (
        <div className="mt-6 print:hidden">
          {filesNotAttached ? (
            <Notice tone="amber" icon={FiAlertCircle}>
              Dossier enregistré, mais les documents n’ont pas pu être joints. Ajoutez-les depuis le bloc Fichiers.
            </Notice>
          ) : (
            <Notice tone="green" icon={FiCheckCircle}>
              Dossier enregistré.
            </Notice>
          )}
        </div>
      )}

      {access.otherUnit && (
        <div className="mt-6 print:hidden">
          <Notice tone="brand" icon={FiInfo}>
            Ce dossier appartient à l’unité <strong>{patient.services || 'non précisée'}</strong> : vous pouvez le consulter
            et y ajouter des mises à jour, mais seule cette unité peut le modifier.
          </Notice>
        </div>
      )}

      <dl className={`${CARD} mb-0 mt-8 grid grid-cols-2 divide-x-0 sm:grid-cols-3 lg:grid-cols-6`}>
        <Fact label="Âge" value={ageLabel(patient)} />
        <Fact label="Sexe" value={patient.genre === 'Feminin' ? 'Féminin' : patient.genre} />
        <Fact label="Groupe sanguin" value={patient.groupeSanguin} />
        <Fact label="Entrée" value={formatDate(patient.dateEntree)} />
        <Fact label="Sortie" value={formatDate(patient.dateSortie)} />
        <Fact label="Mis à jour" value={timeAgo(patient.updatedAt)} />
      </dl>
      {patient.manageur && (
        <p className="mb-0 mt-2 text-right text-[12.5px] text-black/40">Dernière modification par {patient.manageur}</p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-[16px] xl:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-[16px] xl:col-span-2">
          {SECTIONS.filter((section) => !section.clinical).map((section) => (
            <SectionPanel key={section.id} section={section} patient={patient} />
          ))}
          {clinicalEmpty ? (
            <Panel title="Dossier clinique">
              <p className="m-0 text-[14px] text-black/50">
                Aucune donnée clinique n’est encore saisie (motif, examens, intervention, suites).
                {access.canEdit && (
                  <>
                    {' '}
                    <Link to={`/patients/${patient._id}/modifier`} className="font-medium text-brand-600 no-underline hover:text-brand-700">
                      Compléter le dossier
                    </Link>
                  </>
                )}
              </p>
            </Panel>
          ) : (
            SECTIONS.filter((section) => section.clinical).map((section) => (
              <SectionPanel key={section.id} section={section} patient={patient} />
            ))
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-[16px]">
          <PatientNotes patient={patient} user={user} canWrite={access.canWriteNotes} />
          {rights.fichiers && <PatientFiles patient={patient} user={user} rights={rights} />}
          {rights.staff && <PatientStaff patient={patient} user={user} rights={rights} />}
          {rights.echo && <PatientEchos patient={patient} />}
          {rights.cro && <PatientCros patient={patient} user={user} rights={rights} />}
        </div>
      </div>

      <Modal
        open={dialog === 'transfer'}
        title="Transférer le dossier"
        onClose={() => setDialog(null)}
        footer={
          <>
            <button type="button" onClick={() => setDialog(null)} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={transfer} disabled={!unit || busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {busy ? 'Transfert…' : 'Transférer'}
            </button>
          </>
        }
      >
        <p className="mb-[16px] mt-0 text-[14.5px] leading-relaxed text-black/55">
          Le dossier de <strong className="text-ink">{patient.nom}</strong> sera rattaché à l’unité choisie, avec le statut
          « Transféré ». Seule cette unité pourra ensuite le modifier.
        </p>
        <Field id="transfer-unit" label="Unité de destination">
          <Select
            id="transfer-unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="Choisir l’unité…"
            options={UNITS.filter((name) => name.toLowerCase() !== String(patient.services || '').toLowerCase())}
          />
        </Field>
        {dialogError && (
          <div className="mt-[12px]">
            <Notice tone="red">{dialogError}</Notice>
          </div>
        )}
      </Modal>

      <Modal
        open={dialog === 'delete'}
        title="Supprimer le dossier"
        onClose={() => setDialog(null)}
        footer={
          <>
            <button type="button" onClick={() => setDialog(null)} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer définitivement'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          Le dossier de <strong className="text-ink">{patient.nom}</strong> sera supprimé définitivement. Cette action ne
          peut pas être annulée et reste inscrite au journal d’activité.
        </p>
        {dialogError && (
          <div className="mt-[12px]">
            <Notice tone="red">{dialogError}</Notice>
          </div>
        )}
      </Modal>
    </main>
  );
};

export default PatientRecord;
